import { Injectable } from '@angular/core';
import { fromEvent, Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { UAParser } from 'ua-parser-js';

export type SimpleOrientation = 'landscape' | 'portrait';

@Injectable({
  providedIn: 'root',
})
export class DeviceInfoService {
  readonly isTouchDevice: boolean;
  readonly isMobile: boolean;
  readonly isTablet: boolean;
  readonly isAndroid: boolean;
  readonly isIOS: boolean;
  readonly browserDescription: string;
  readonly deviceDescription: string;
  readonly osDescription: string;
  private readonly _hasScreenOrientationApi: boolean;
  private readonly _hasDeprecatedWindowOrientationApi: boolean;

  constructor() {
    // determine device information that do not change during runtime in constructor
    const ua = new UAParser();
    const type = ua.getDevice().type;
    this.isMobile = type === 'mobile';
    this.isTablet = type === 'tablet';
    this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    this._hasScreenOrientationApi = !(screen.orientation === undefined);
    this._hasDeprecatedWindowOrientationApi = !(window.screen.orientation === undefined);
    const os = ua.getOS();
    this.isAndroid = os.name?.startsWith('Android') ?? false;
    this.isIOS = os.name === 'iOS';
    this.deviceDescription = `${ua.getDevice().type} ${ua.getDevice().vendor} ${ua.getDevice().model}`;
    this.browserDescription = `${ua.getBrowser().name} ${ua.getBrowser().version}`;
    this.osDescription = `${ua.getOS().name} ${ua.getOS().version}`;
  }

  public get isMobileOrTablet(): boolean {
    return this.isMobile || this.isTablet;
  }

  public get screenOrientation(): SimpleOrientation {
    if (this._hasScreenOrientationApi) {
      return this.orientationType2SimpleOrientation(screen.orientation.type);
    } else if (this._hasDeprecatedWindowOrientationApi) {
      return this.deprecatedWindowOrientation2SimpleOrientation(window.orientation);
    } else {
      return screen.width > screen.height ? 'landscape' : 'portrait';
    }
  }

  public get screenOrientation$(): Observable<SimpleOrientation> {
    if (this._hasScreenOrientationApi) {
      return fromEvent(screen.orientation, 'change').pipe(
        map(() => this.screenOrientation),
        startWith(this.screenOrientation),
      );
    } else if (this._hasDeprecatedWindowOrientationApi) {
      return fromEvent(window, 'orientationchange').pipe(
        map(() => this.screenOrientation),
        startWith(this.screenOrientation),
      );
    } else {
      return of(this.screenOrientation);
    }
  }

  private orientationType2SimpleOrientation(type: OrientationType): SimpleOrientation {
    return type.includes('landscape') ? 'landscape' : 'portrait';
  }

  private deprecatedWindowOrientation2SimpleOrientation(o: string | number): SimpleOrientation {
    switch (o) {
      case 0:
      case '0':
      case 180:
      case '180': {
        return 'portrait';
      }
      case -90:
      case '-90':
      case 90:
      case '90': {
        return 'landscape';
      }
    }
    return 'landscape';
  }
}
